import { useCallback, useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";

/**
 * Reads a Books endpoint. Keeps the previous result on screen while a new one
 * loads (no flash on every filter change) and recognises "Tally is not
 * connected" so pages can show the way to connect instead of an error.
 */
export function useBooks(path, params = {}, { enabled = true } = {}) {
  const [state, setState] = useState({ data: null, loading: true, error: null, notConnected: false });
  const key = JSON.stringify([path, params]);
  const seq = useRef(0);

  const load = useCallback(async () => {
    if (!enabled || !path) return;
    const mine = ++seq.current;
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get(path, { params: JSON.parse(key)[1] });
      if (mine === seq.current) setState({ data, loading: false, error: null, notConnected: false });
    } catch (e) {
      if (mine !== seq.current) return;
      const notConnected = e?.response?.status === 404 && e?.response?.data?.detail?.code === "TALLY_NOT_CONNECTED";
      setState((s) => ({ ...s, loading: false, error: notConnected ? null : apiError(e), notConnected }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, reload: load };
}

/** Downloads a report export (PDF / Excel / CSV) with the session cookie. */
export async function download(path, params, format) {
  const response = await api.get(path, { params: { ...params, format }, responseType: "blob" });
  const disposition = response.headers["content-disposition"] || "";
  const name = /filename="([^"]+)"/.exec(disposition)?.[1] || `report.${format}`;
  const url = URL.createObjectURL(response.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ---------------------------------------------------------------- money
const amountFmt = new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const compactFmt = new Intl.NumberFormat("en-IN", { notation: "compact", maximumFractionDigits: 1 });

/** 1234567.5 -> "12,34,567.50" */
export function amount(value) {
  const n = Number(value || 0);
  return amountFmt.format(Math.abs(n) < 0.005 ? 0 : n);
}

/** 1234567.5 -> "₹12,34,567.50" (negative as "-₹…") */
export function money(value) {
  const n = Number(value || 0);
  return `${n < 0 ? "-" : ""}₹${amount(Math.abs(n))}`;
}

/** Axis ticks: "₹12.3L" style compact ("-₹4L" below zero). */
export function compactMoney(value) {
  const n = Number(value || 0);
  const a = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  const short = (x) => x.toFixed(1).replace(/\.0$/, "");
  if (a >= 1e7) return `${sign}₹${short(a / 1e7)}Cr`;
  if (a >= 1e5) return `${sign}₹${short(a / 1e5)}L`;
  if (a >= 1e3) return `${sign}₹${short(a / 1e3)}K`;
  return `${sign}₹${compactFmt.format(a)}`;
}

/** A debit-positive balance as Tally shows it: "₹1,200.00 Dr" / "₹500.00 Cr". */
export function drcr(value) {
  const n = Number(value || 0);
  if (Math.abs(n) < 0.005) return "₹0.00";
  return `₹${amount(Math.abs(n))} ${n > 0 ? "Dr" : "Cr"}`;
}

// ---------------------------------------------------------------- periods
const iso = (d) => {
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d - tz).toISOString().slice(0, 10);
};

/** Date presets, in a financial year starting in `fyMonth` (1-12, April = 4). */
export function periodPresets(fyMonth = 4, now = new Date()) {
  const y = now.getFullYear();
  const m = now.getMonth(); // 0-based
  const fyStartYear = m + 1 >= fyMonth ? y : y - 1;
  const fyStart = new Date(fyStartYear, fyMonth - 1, 1);
  const fyEnd = new Date(fyStartYear + 1, fyMonth - 1, 0);
  const qStartMonth = m - ((m - (fyMonth - 1) + 12) % 3);
  return [
    { key: "today", label: "Today", from: iso(now), to: iso(now) },
    { key: "this_month", label: "This month", from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 0)) },
    { key: "last_month", label: "Last month", from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)) },
    { key: "this_quarter", label: "This quarter", from: iso(new Date(y, qStartMonth, 1)), to: iso(new Date(y, qStartMonth + 3, 0)) },
    { key: "this_fy", label: "This financial year", from: iso(fyStart), to: iso(fyEnd) },
    { key: "last_fy", label: "Last financial year", from: iso(new Date(fyStartYear - 1, fyMonth - 1, 1)), to: iso(new Date(fyStartYear, fyMonth - 1, 0)) },
  ];
}

/** Periods for the home page cards: whole months, keyed for the card menus. */
export function homePeriods(fyMonth = 4, now = new Date()) {
  const y = now.getFullYear();
  const m = now.getMonth(); // 0-based
  const fyStartYear = m + 1 >= fyMonth ? y : y - 1;
  const q = m - ((m - (fyMonth - 1) + 12) % 3);
  const months = (a, b) => ({ from: iso(new Date(y, a, 1)), to: iso(new Date(y, b + 1, 0)) });
  const fy = (start) => ({ from: iso(new Date(start, fyMonth - 1, 1)), to: iso(new Date(start + 1, fyMonth - 1, 0)) });
  return {
    this_fy: { label: "This Financial Year", ...fy(fyStartYear) },
    this_quarter: { label: "This Quarter", ...months(q, q + 2) },
    this_month: { label: "This Month", ...months(m, m) },
    last_fy: { label: "Previous Financial Year", ...fy(fyStartYear - 1) },
    last_quarter: { label: "Previous Quarter", ...months(q - 3, q - 1) },
    last_month: { label: "Previous Month", ...months(m - 1, m - 1) },
    last_6: { label: "Last 6 Months", ...months(m - 5, m) },
    last_12: { label: "Last 12 Months", ...months(m - 11, m) },
  };
}

/** First day of the financial year containing `value` ("YYYY-MM-DD"). */
export function fyStartOf(value, fyMonth = 4) {
  const [y, m] = value.split("-").map(Number);
  const year = m >= fyMonth ? y : y - 1;
  return `${year}-${String(fyMonth).padStart(2, "0")}-01`;
}

export function todayISO() {
  return iso(new Date());
}

/** "2024-04-01" -> "01 Apr 2024" */
export function dateLabel(value) {
  if (!value) return "—";
  const [y, m, d] = value.split("-").map(Number);
  if (!y) return value;
  return new Date(y, m - 1, d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2024-04" -> "Apr 24" (tooltips, multi-year axes) */
export function shortMonth(value) {
  const [y, m] = value.split("-").map(Number);
  return `${MONTHS[m - 1]} ${String(y).slice(2)}`;
}

/** "2024-04" -> "Apr" (axis of a chart that spans one financial year) */
export function monthName(value) {
  return MONTHS[Number(value.split("-")[1]) - 1];
}
